const clean = (value = '') => String(value).replace(/\s+/g, ' ').trim();

const firstMatch = (text, pattern) => {
  const match = text.match(pattern);
  return match?.[1] ? clean(match[1]) : '';
};

const rowsAfter = (text, heading, stopHeading) => {
  const start = text.search(heading);
  if (start < 0) return '';
  const rest = text.slice(start);
  const stop = stopHeading ? rest.search(stopHeading) : -1;
  return stop > 0 ? rest.slice(0, stop) : rest;
};

const splitRow = (line) => line.includes('|')
  ? line.split('|').map(clean).filter(Boolean)
  : line.split('\t').map(clean).filter(Boolean);

const parseReferenceShares = (text) => {
  const block = rowsAfter(text, /Information relating to the Reference Shares/i, /Page \d+ of \d+/i);
  return block.split('\n').map((line) => {
    const cells = splitRow(line);
    if (cells.length >= 7 && /^\d+$/.test(cells[0])) {
      const initial = firstMatch(cells[5], /(?:USD\s*)?([\d,.]+)/i);
      const strike = firstMatch(cells[6], /(?:USD\s*)?([\d,.]+)/i);
      const knockIn = firstMatch(cells[7] || '', /(?:USD\s*)?([\d,.]+)/i);
      return { entity: cells[1], ticker: cells[3], initial, strike, knockIn };
    }
    // Some OCR engines flatten the final reference-share table into spaces instead
    // of retaining tabs/pipes. Preserve the actual issuer/ticker/three price fields.
    const flattened = clean(line).match(/^\d+\s+(.+?)\s+(?:All Exchanges of|Common Stock of)\s+.+?\s+([A-Z]{1,8}\s+UQ(?:\s+EQUITY)?)\s+.+?\s+USD\s+([\d.]+)\s+USD\s+([\d.]+)\s+USD\s+([\d.]+)$/i);
    if (flattened) {
      return { entity: flattened[1], ticker: flattened[2], initial: flattened[3], strike: flattened[4], knockIn: flattened[5] };
    }
    return null;
  }).filter(Boolean);
};

const parseTriggerLevels = (text) => {
  const block = rowsAfter(text, /Trigger Level Schedule/i, /Fixed Coupon Payment Dates:/i);
  return block.split('\n').map((line) => {
    const cells = splitRow(line);
    const match = line.match(/Valuation Date\s*\((\d+)\).*?(\d+(?:\.\d+)?)(?:\s*[|\t]\s*|\s{2,})(\d+(?:\.\d+)?)(?:\s*[|\t]\s*|\s{2,})(\d+(?:\.\d+)?)/i);
    if (cells.length >= 4 && /Valuation Date/i.test(cells[0])) {
      return { index: Number(firstMatch(cells[0], /\((\d+)\)/)), level: Number(cells[1]), values: cells.slice(2).map(Number) };
    }
    if (match) return { index: Number(match[1]), level: Number(match[2]), values: [Number(match[3]), Number(match[4])] };
    return null;
  }).filter((row) => Number.isFinite(row?.level));
};

const parseDates = (text) => {
  const valuationBlock = rowsAfter(text, /Valuation Dates:/i, /Trigger Levels:/i);
  const valuationDates = [...valuationBlock.matchAll(/(?:^|\n)\s*(\d+)\s*(?:\||\t|\s+)\s*(\d{1,2}\s+[A-Za-z]+\s+\d{4})/g)]
    .map((match) => ({ index: Number(match[1]), date: clean(match[2]) }));
  const paymentBlock = rowsAfter(text, /scheduled Fixed Coupon Payment Dates/i, /Information relating to the Reference Shares/i);
  const paymentDates = [...paymentBlock.matchAll(/(?:^|\n)\s*(\d+)\s*(?:\||\t|\s+)\s*(\d{1,2}\s+[A-Za-z]+\s+\d{4})/g)]
    .map((match) => ({ index: Number(match[1]), date: clean(match[2]) }));
  return { valuationDates, paymentDates };
};

export const parseStructuredProduct = (terms) => {
  const text = (terms?.pages || []).map((page) => page.raw_text || '').join('\n');
  const couponRate = Number(firstMatch(text, /Fixed Coupon Rate:\s*([\d.]+)%/i)) || Number(terms?.fixed_coupon_rate || 0) * 100;
  const tradeDate = firstMatch(text, /Trade Date:\s*([^\n]+)/i) || terms?.trade_date || '';
  const startDate = firstMatch(text, /Start Date:\s*([^\n]+)/i) || terms?.start_date || '';
  const maturityDate = firstMatch(text, /Cash Settlement Date:\s*([^,\n]+)/i) || terms?.maturity_date || '';
  const strikePercent = Number(firstMatch(text, /Strike Price(?:\([^)]*\))?\s*\((\d+(?:\.\d+)?)%/i));
  const knockInPercent = Number(firstMatch(text, /Knock-In Price(?:\([^)]*\))?\s*\((\d+(?:\.\d+)?)%/i));
  const parsedDates = parseDates(text);
  const triggerLevels = parseTriggerLevels(text).map((row) => ({
    ...row,
    date: parsedDates.valuationDates.find((item) => item.index === row.index)?.date || ''
  }));
  return {
    couponRate,
    tradeDate,
    startDate,
    maturityDate,
    strikePercent,
    knockInPercent,
    referenceShares: parseReferenceShares(text),
    triggerLevels,
    ...parsedDates
  };
};
